import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/Button';
import { toast } from '@/utils/toast';
import { ICONS } from '@/constants/icons';
import { DEFAULT_IMAGES } from '@/constants/whitelabel-defaults';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { mediaService } from '@/services/media.service';
import apiClient from '@/services/api';
import { devLog } from '@/utils/logger';
import { Card, CardHeader, CardBody, Badge, Icon } from '@/components/ui';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';

/**
 * AdminBranding Page
 *
 * Manage system default branding images via SystemSettings.
 * These are fallback images used when teams/agencies don't have custom branding.
 */

interface BrandingConfig {
  key: string;
  label: string;
  defaultKey: keyof typeof DEFAULT_IMAGES;
  size: string;
}

const BRANDING_KEYS: BrandingConfig[] = [
  { key: 'branding_dashboard_header', label: 'Dashboard Header', defaultKey: 'dashboard_header', size: '1920x400px' },
  { key: 'branding_dashboard_footer', label: 'Dashboard Footer', defaultKey: 'dashboard_footer', size: '1920x100px' },
  { key: 'branding_calendar_header', label: 'Calendar Header', defaultKey: 'calendar_header', size: '1920x400px' },
  { key: 'branding_list_header', label: 'Post List Header', defaultKey: 'list_header', size: '1920x400px' },
  { key: 'branding_brandcheck_header', label: 'Brand Check Header', defaultKey: 'brandcheck_header', size: '1920x400px' },
  { key: 'branding_release_header', label: 'Release Header', defaultKey: 'release_header', size: '1920x400px' },
  { key: 'branding_agencycc_header', label: 'Brand Portfolio Header', defaultKey: 'agencycc_header', size: '1920x400px' },
  { key: 'branding_approval_header', label: 'Approval Header', defaultKey: 'approval_header', size: '1920x400px' },
  { key: 'branding_logo_big', label: 'Logo (Large)', defaultKey: 'logo_big', size: '400x100px' },
  { key: 'branding_logo_small', label: 'Logo (Small)', defaultKey: 'logo_small', size: '60x60px' },
];

export const AdminBranding = () => {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingUploadKey, setPendingUploadKey] = useState<string | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/api/admin/settings?category=branding');
      const settingsMap: Record<string, string> = {};
      response.data.data?.forEach((s: { key: string; value: string }) => {
        settingsMap[s.key] = s.value;
      });
      setSettings(settingsMap);
    } catch (err: unknown) {
      // Ignore cancellation errors (React StrictMode double-mount)
      if (err && typeof err === 'object' && 'code' in err && err.code === 'ERR_CANCELED') {
        return;
      }
      toast.error('Failed to load branding settings');
      devLog.error('Error fetching branding settings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (key: string) => {
    setIsSaving(true);
    try {
      await apiClient.patch(`/api/admin/settings/${key}`, {
        value: editValue,
        category: 'branding',
        description: `System default branding: ${key}`,
      });
      toast.success('Branding image updated');
      setSettings(prev => ({ ...prev, [key]: editValue }));
      setEditingKey(null);
      setEditValue('');
    } catch (err) {
      toast.error('Failed to save branding image');
      devLog.error('Error saving branding setting:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async (key: string) => {
    setIsSaving(true);
    try {
      await apiClient.patch(`/api/admin/settings/${key}`, {
        value: '',
        category: 'branding',
      });
      toast.success('Reset to default');
      setSettings(prev => {
        const newSettings = { ...prev };
        delete newSettings[key];
        return newSettings;
      });
    } catch (err) {
      toast.error('Failed to reset');
      devLog.error('Error resetting branding setting:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input for future uploads
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingKey(key);
    try {
      // Upload to S3. Branding logos are never attached to a post (no
      // PostMedia row), so they never count toward the team's storage
      // quota — skip the check.
      const uploadResult = await mediaService.directUpload(file, { skipStorageCheck: true });
      const imageUrl = uploadResult.optimizedUrl || uploadResult.originalUrl;

      // Save to settings using apiClient (not fetch)
      await apiClient.patch(`/api/admin/settings/${key}`, {
        value: imageUrl,
        category: 'branding',
        description: `System default branding: ${key}`,
      });

      toast.success('Image uploaded and saved');
      setSettings(prev => ({ ...prev, [key]: imageUrl }));
    } catch (err) {
      toast.error('Failed to upload image');
      devLog.error('Error uploading image:', err);
    } finally {
      setUploadingKey(null);
    }
  };

  const triggerUpload = (key: string) => {
    setPendingUploadKey(key);
    fileInputRef.current?.click();
  };

  const getImageUrl = (config: BrandingConfig): string => {
    const customValue = settings[config.key];
    if (customValue && customValue.trim()) return customValue;
    return DEFAULT_IMAGES[config.defaultKey] || '';
  };

  const isCustom = (key: string): boolean => {
    return !!(settings[key] && settings[key].trim() !== '');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          if (pendingUploadKey) {
            handleFileSelect(e, pendingUploadKey);
            setPendingUploadKey(null);
          }
        }}
      />

      {/* Header */}
      <AdminPageHeader
        title="System Branding"
        subtitle="Manage default branding images for all users"
      />

      {/* Info Banner */}
      <div className="bg-soft-mint border border-light-teal-gray rounded-lg p-4">
        <p className="text-sm text-dark-teal">
          <strong>Note:</strong> These are the system default images used when teams/agencies don't have their own custom branding.
          Enter image URLs to override defaults or upload images directly.
        </p>
      </div>

      {/* Branding Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {BRANDING_KEYS.map(config => (
          <Card key={config.key} padding="none" className="overflow-hidden">
            {/* Card Header */}
            <CardHeader padding="sm" className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{config.label}</h3>
                <p className="text-xs text-gray-500">{config.size}</p>
              </div>
              {isCustom(config.key) && (
                <Badge variant="success">Custom</Badge>
              )}
            </CardHeader>

            {/* Card Body */}
            <CardBody padding="sm">
              {/* Image Preview */}
              <div className="mb-3 bg-gray-100 rounded overflow-hidden">
                <img
                  src={getImageUrl(config)}
                  alt={config.label}
                  className="w-full h-32 object-contain"
                  onError={(e) => {
                    // Fallback if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.src = '/assets/branding/misc/placeholder.png';
                  }}
                />
              </div>

              {/* Edit/View Mode */}
              {editingKey === config.key ? (
                <div className="space-y-3">
                  <input
                    type="url"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    placeholder="Enter image URL"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary-400"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleSave(config.key)}
                      loading={isSaving}
                      disabled={!editValue.trim()}
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setEditingKey(null);
                        setEditValue('');
                      }}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setEditingKey(config.key);
                      setEditValue(settings[config.key] || '');
                    }}
                    disabled={uploadingKey !== null}
                  >
                    <Icon name={ICONS.EDIT} size="custom" className="mr-1" />
                    Edit URL
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => triggerUpload(config.key)}
                    loading={uploadingKey === config.key}
                    disabled={uploadingKey !== null && uploadingKey !== config.key}
                  >
                    <Icon name={ICONS.IMAGE} size="custom" className="mr-1" />
                    {uploadingKey === config.key ? 'Uploading...' : 'Upload'}
                  </Button>
                  {isCustom(config.key) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleReset(config.key)}
                      disabled={isSaving || uploadingKey !== null}
                    >
                      <Icon name={ICONS.REFRESH} size="custom" className="mr-1" />
                      Reset
                    </Button>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminBranding;
